import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, finalize, map, takeUntil} from 'rxjs/operators';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {BusinessDetails, MerchantVerificationResponse, WebBusinessService} from 'web-angular-client';

import {ROUTES} from '../../app.routes';
import {ASecureForm} from '../../sdk/interface/form/ASecureForm';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {AlertService} from '../../sdk/service/alert.service';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {EcommerceVerificationService} from '../../sdk/service/ecommerce-verification.service';
import {EcommerceVerificationState} from '../../sdk/interface/ecommerce-verification-state';
import {copyWithFeedback} from '../../sdk/util/copy-feedback';
import {
    deriveAvsCardState,
    deriveKycCardState,
    VerificationCardState
} from './verification-card/verification-card-state';

/**
 * Landing page for e-commerce merchants who have not completed KYC and AVS verification.
 * Shows the verification progress, a card per verification with its current state, and the
 * External Identifier with a link to the setup guide, so the merchant can connect a store in sandbox mode in the
 * meantime.
 */
@Component({
    selector: 'app-ecommerce-landing',
    templateUrl: './ecommerce-landing.component.html',
    styleUrls: ['./ecommerce-landing.component.scss'],
    standalone: false
})
export class EcommerceLandingComponent extends ASecureForm implements OnInit, OnDestroy
{
    kycCardState: VerificationCardState = 'NOT_STARTED';
    avsCardState: VerificationCardState = 'NOT_STARTED';
    readonly accountSettingsPath = ROUTES.SECURE.BUSINESS.EDIT.path;
    externalIdentifier: string;
    businessDetailsLoadFailed = false;
    regeneratingKycLink = false;
    kycLinkRegenerationEligible = false;
    copiedFields: { [key: string]: boolean } = {};
    private readonly destroy$ = new Subject<void>();
    private readonly copiedResetTimers: { [key: string]: ReturnType<typeof setTimeout> } = {};

    constructor(
        private router: Router,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webBusinessService: WebBusinessService,
        private ecommerceVerificationService: EcommerceVerificationService,
        private alertService: AlertService,
        private ngxLoader: NgxUiLoaderService)
    {
        super(authenticationService, environmentService);
    }

    /**
     * Progress-banner heading and message for the current card states, so FAILED/ERROR don't read as
     * "pending". A failure on either card takes precedence — an under-review KYC with a failed AVS must
     * still read as failed — followed by a load failure on either card.
     */
    get bannerCopy(): { heading: string; message: string }
    {
        if (this.kycCardState === 'FAILED' || this.avsCardState === 'FAILED')
        {
            return {heading: 'Verification Failed', message: 'please review your details and resubmit'};
        }
        if (this.kycCardState === 'ERROR' || this.avsCardState === 'ERROR')
        {
            return {heading: 'Verification status unavailable', message: 'please refresh to try again'};
        }
        if (this.kycCardState === 'UNDER_REVIEW')
        {
            return {heading: 'Verification Under Review', message: 'our compliance team is checking your submission'};
        }
        return {heading: 'Verification Pending', message: 'one step to go'};
    }

    /** The KYC card's action label for its state; no action while failed, errored or verified. */
    get kycCardActionLabel(): string
    {
        switch (this.kycCardState)
        {
            case 'NOT_STARTED':
                return 'Start verification';
            case 'UNDER_REVIEW':
                return 'View submission';
            default:
                return null;
        }
    }

    /** The KYC card's secondary action label: offer the merchant their verification link while under review and eligible. */
    get kycCardSecondaryActionLabel(): string
    {
        return this.kycCardState === 'UNDER_REVIEW' && this.kycLinkRegenerationEligible ? 'Get your verification link' : null;
    }

    /**
     * Loads the verification states and External Identifier shown on the page. Non e-commerce merchants are redirected to their home screen; e-commerce
     * merchants who no longer need the landing page (KYC verified, or pending while not a guest admin) are
     * redirected to the e-commerce dashboard rather than being shown the onboarding page. Users without
     * BUSINESS_UPDATE_FICA_DETAILS (Admins, Field Agents) cannot complete KYC, so they skip the check and are sent
     * to their home screen too.
     */
    ngOnInit(): void
    {
        if (!this.authenticationService.getUserLogin()?.eCommerceMerchant
            || !this.hasRouteAccess(ROUTES.SECURE.BUSINESS.EDIT_FICA_DETAILS))
        {
            this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]).then();
            return;
        }

        this.ngxLoader.start();
        forkJoin([
            this.ecommerceVerificationService.getVerificationState(),
            this.webBusinessService.getBusinessDetails().pipe(
                catchError((error) =>
                {
                    this.debugOutput('Failed to load business details for the ecommerce landing page', error);
                    this.businessDetailsLoadFailed = true;
                    return of({} as BusinessDetails);
                })),
            this.webBusinessService.getKycLinkRegenerationEligibility().pipe(
                map((eligibility) => eligibility?.eligible ?? false),
                catchError((error) =>
                {
                    this.debugOutput('Failed to load KYC link regeneration eligibility', error);
                    return of(false);
                }))
        ]).pipe(takeUntil(this.destroy$), finalize(() => this.ngxLoader.stop())).subscribe({
            next: ([verificationState, businessDetails, kycLinkRegenerationEligible]:
                   [EcommerceVerificationState, BusinessDetails, boolean]) =>
            {
                if (!this.ecommerceVerificationService.shouldDisplayLandingPage(
                    verificationState,
                    this.isGuestAdminUser()))
                {
                    this.router.navigate([ROUTES.SECURE.ACCOUNT.ECOMMERCE_DASHBOARD.path]).then();
                    return;
                }
                this.kycCardState = deriveKycCardState(verificationState.kycStatus, verificationState.kycLoadFailed);
                this.avsCardState = deriveAvsCardState(
                    verificationState.avsVerificationStatus, verificationState.avsLoadFailed);
                this.externalIdentifier = businessDetails?.externalIdentifier;
                this.kycLinkRegenerationEligible = kycLinkRegenerationEligible;
            },
            error: () =>
            {
                // A total load failure leaves the External Identifier unavailable too, so surface the refresh hint.
                this.businessDetailsLoadFailed = true;
                this.alertService.error(
                    'An error occurred while loading your verification status. Please try again later.', true);
            }
        });
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
        Object.values(this.copiedResetTimers).forEach(timer => clearTimeout(timer));
    }

    /**
     * Dispatches the KYC card's action by its state: not-started starts the verification wizard,
     * an under-review submission opens it read-only. Other states render no action button.
     */
    onKycCardAction(): void
    {
        if (this.kycCardState === 'UNDER_REVIEW')
        {
            this.viewSubmission();
            return;
        }
        this.startVerification();
    }

    /**
     * Opens the KYC verification wizard.
     */
    startVerification(): void
    {
        this.router.navigate([ROUTES.SECURE.ECOMMERCE.KYC.path]).then();
    }

    /**
     * Opens the read-only view of the submitted KYC verification.
     */
    viewSubmission(): void
    {
        this.router.navigate([ROUTES.SECURE.ECOMMERCE.KYC_VIEW.path]).then();
    }

    /**
     * Requests a fresh KYC verification link for a merchant whose verification is under review — for when the
     * original link was never received, was lost, or has expired — and shows the new link on success. The backend's
     * message (not eligible, cap reached, or service offline) is surfaced on error.
     */
    regenerateKycLink(): void
    {
        if (this.regeneratingKycLink)
        {
            return;
        }
        this.regeneratingKycLink = true;
        this.ngxLoader.start();
        this.webBusinessService.regenerateKycLink().pipe(
            takeUntil(this.destroy$),
            finalize(() =>
            {
                this.ngxLoader.stop();
                this.regeneratingKycLink = false;
            })).subscribe({
            next: (response: MerchantVerificationResponse) =>
            {
                const kycVerificationUrl = response?.kycVerificationUrl?.trim();
                if (!kycVerificationUrl)
                {
                    this.alertService.error('We could not retrieve your verification link. Please try again.');
                    return;
                }
                this.kycLinkRegenerationEligible = false;
                this.showKycLinkPopup(kycVerificationUrl);
            },
            error: (error) => this.alertService.error(error)
        });
    }

    /**
     * Copies the given value to the clipboard and briefly flags the field as copied. Surfaces an
     * error if the clipboard is unavailable (e.g. permission denied or an insecure context).
     *
     * @param value the value to copy
     * @param field the field the value was copied from, used to toggle its "copied" indicator
     */
    copyToClipboard(value: string, field: string): void
    {
        copyWithFeedback(
            value,
            field,
            this.copiedFields,
            this.copiedResetTimers,
            () => this.alertService.error('Could not copy to the clipboard. Please copy the value manually.'));
    }

    /** The setup guide covering how to connect a store, test in sandbox mode and go live. */
    get setupGuideLink(): string
    {
        return this.environmentService.setupGuideLink;
    }

    /**
     * Shows the merchant's KYC verification link in the shared success modal as a "Verify your identity" action. Used
     * both for a freshly regenerated link and for an existing still-valid link returned as-is; the copy points to the
     * on-screen button rather than implying a fresh email was sent (a still-valid link is not re-emailed).
     *
     * @param kycVerificationUrl the verification link to present
     */
    private showKycLinkPopup(kycVerificationUrl: string): void
    {
        this.alertService.success(
            `Here's your verification link.
                 <br/>
                 <br/>
                 <strong>Complete your identity verification using the button below.</strong>`,
            false,
            undefined,
            {
                type: 'link',
                url: kycVerificationUrl,
                label: 'Verify Your Identity',
                cssClass: 'selfie-verify-btn',
                openInNewTab: true
            });
    }
}
